import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { ScrollArea } from '../ui/scroll-area';
import {
  LayoutDashboard,
  Building2,
  Store,
  Box,
  MapPin,
  BarChart3,
  Users,
  Sparkles,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Building,
  UserCircle,
  Briefcase,
  FileText,
  Calculator,
  Target,
  Receipt,
  Wrench,
} from 'lucide-react';

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['super_admin', 'group_admin', 'mall_admin', 'leasing_head', 'finance', 'ops', 'marketing'] },
  { name: 'Mall Groups', href: '/groups', icon: Building, roles: ['super_admin'] },
  { name: 'Malls', href: '/malls', icon: Building2, roles: ['super_admin', 'group_admin', 'mall_admin'] },
  { name: 'Inventory', href: '/inventory', icon: Store, roles: ['super_admin', 'group_admin', 'mall_admin', 'leasing_head', 'leasing_executive'] },
  { name: 'Digital Twin', href: '/digital-twin', icon: Box, roles: ['super_admin', 'group_admin', 'mall_admin', 'ops'] },
  { name: 'Pre-Leasing', href: '/pre-leasing', icon: Target, roles: ['super_admin', 'group_admin', 'mall_admin', 'leasing_head', 'leasing_executive'] },
  { name: 'Post-Leasing', href: '/post-leasing', icon: Receipt, roles: ['super_admin', 'group_admin', 'mall_admin', 'finance', 'ops'] },
  { name: 'Operations', href: '/operations', icon: Wrench, roles: ['super_admin', 'group_admin', 'mall_admin', 'ops'] },
  { name: 'Leasing CRM', href: '/leasing', icon: Briefcase, roles: ['super_admin', 'group_admin', 'mall_admin', 'leasing_head', 'leasing_executive'] },
  { name: 'Proposals', href: '/proposals', icon: Calculator, roles: ['super_admin', 'group_admin', 'mall_admin', 'leasing_head', 'leasing_executive'] },
  { name: 'Documents', href: '/documents', icon: FileText, roles: ['super_admin', 'group_admin', 'mall_admin', 'leasing_head', 'finance'] },
  { name: 'Tenant Portal', href: '/tenant-portal', icon: UserCircle, roles: ['super_admin', 'group_admin', 'mall_admin', 'brand_partner'] },
  { name: 'Analytics', href: '/analytics', icon: BarChart3, roles: ['super_admin', 'group_admin', 'mall_admin', 'finance', 'marketing'] },
  { name: 'Users', href: '/users', icon: Users, roles: ['super_admin', 'group_admin', 'mall_admin'] },
  { name: 'AI Insights', href: '/ai-insights', icon: Sparkles, roles: ['super_admin', 'group_admin', 'mall_admin', 'leasing_head'] },
  { name: 'Settings', href: '/settings', icon: Settings, roles: ['super_admin', 'group_admin', 'mall_admin'] },
];

const roleLabels = {
  super_admin: 'Super Admin',
  group_admin: 'Group Admin',
  mall_admin: 'Mall Admin',
  leasing_head: 'Leasing Head',
  leasing_executive: 'Leasing Executive',
  finance: 'Finance',
  ops: 'Operations',
  marketing: 'Marketing',
  advertiser: 'Advertiser',
  brand_partner: 'Brand Partner',
  broker: 'Broker',
  investor: 'Investor',
};

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const filteredNav = navigation.filter(
    (item) => item.roles.includes(user?.role) || user?.role === 'super_admin'
  );

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || 'U';
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-card/95 backdrop-blur-xl border-r border-border/40 transition-transform duration-300 lg:relative lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-between px-6 border-b border-border/40">
            <Link to="/" className="flex items-center gap-2">
              <img src="/fundle-logo.png" alt="Fundle" className="h-7 w-auto" />
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 px-3 py-4">
            <nav className="space-y-1">
              {filteredNav.map((item) => {
                const isActive = location.pathname === item.href || 
                  (item.href !== '/' && location.pathname.startsWith(item.href));
                const Icon = item.icon;

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group ${
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    }`}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-primary' : ''}`} />
                    <span className="flex-1">{item.name}</span>
                    {isActive && (
                      <ChevronRight className="w-4 h-4 text-primary" />
                    )}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>

          {/* User section */}
          <div className="p-4 border-t border-border/40">
            <div className="flex items-center gap-3 px-2">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary/20 text-primary text-sm">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {roleLabels[user?.role] || user?.role}
                </p>
              </div>
            </div>
            <div className="mt-3 flex items-center justify-center gap-1.5 opacity-50">
              <img src="/fundle-logo.png" alt="Fundle" className="h-4 w-auto" />
              <span className="text-[10px] text-muted-foreground">PropTech OS</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="h-16 border-b border-border/40 bg-background/80 backdrop-blur-md sticky top-0 z-30">
          <div className="flex h-full items-center justify-between px-4 lg:px-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-semibold">
                  {filteredNav.find((n) => n.href === location.pathname || 
                    (n.href !== '/' && location.pathname.startsWith(n.href)))?.name || 'Dashboard'}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-primary/20 text-primary text-sm">
                        {getInitials(user?.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline text-sm font-medium">
                      {user?.name}
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col">
                      <span>{user?.name}</span>
                      <span className="text-xs text-muted-foreground font-normal">
                        {user?.email}
                      </span>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-auto p-4 lg:p-6" data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
  );
}
